'use client';

import Container from '@mui/material/Container';
import {useParams, useRouter} from 'next/navigation';
import type {ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {
  trackCountryCardClick,
  trackCountryLearnMoreClick,
  trackCountryRegisterInterestClick,
  trackCountryViewItemClick,
} from '../analytics';
import {BannerCard} from '../components/banner-card';
import {ListingCard, ListingEmptyState, ListingGrid, ListingPagination, LOCATION_ICON} from '../components/listing';
import {Section, Typography} from '../components/primitives';
import {ProjectVerticalSection} from '../components/project-vertical-section';
import {openRegisterInterest} from '../components/register-interest/register-interest-controller';
import {SuppressMobileRegisterInterest} from '../components/register-interest/SuppressMobileRegisterInterest';
import {COUNTRIES_EMPTY} from '../content';
import {usePagination} from '../hooks';
import {CountryListType, type CountryDetailView, type ProjectCardView} from '../model';
import {projectHref} from '../routing';

export interface CountryDetailPageProps {
  country: CountryDetailView;
  projects: ProjectCardView[];
}

export function CountryDetailPage({country, projects}: CountryDetailPageProps) {
  const {t} = useTranslation();
  const router = useRouter();
  const {locale} = useParams<{locale: string}>();
  // Real-market projects paginate at 9 per page (catalog); the page selector only
  // shows past one page. Called unconditionally to satisfy rules-of-hooks.
  const projectsPagination = usePagination(projects);

  const banner = <BannerCard title={country.name} imageSrc={country.banner.src} imageAlt={country.banner.alt ?? country.name} />;

  if (country.type === CountryListType.EXTERNAL_REDIRECT && country.features.length > 0) {
    return (
      <>
        <SuppressMobileRegisterInterest />
        {banner}
        {country.features.map((feature, index) => (
          <ProjectVerticalSection
            key={feature.uuid}
            title={feature.name}
            paragraphs={feature.paragraphs}
            image={feature.image.src}
            imageAlt={feature.name}
            mediaSide={index % 2 === 1 ? 'start' : 'end'}
            tone={index % 2 === 1 ? 'subtle' : 'default'}
            action={
              feature.externalUrl
                ? {
                    label: t('learn_more'),
                    href: feature.externalUrl,
                    onClick: () => {
                      trackCountryLearnMoreClick({id: feature.uuid, name: feature.name}, feature.externalUrl);
                    },
                  }
                : undefined
            }
          />
        ))}
      </>
    );
  }

  const heading = (
    <Container>
      <Typography
        size="title-3"
        weight="bold"
        mobileSize="title-5"
        mobileWeight="bold"
        as="h2"
        sx={{textTransform: 'capitalize', color: theme => theme.palette.colors.text.neutral.strong, mb: 3}}
      >
        {t('projects')}
      </Typography>
    </Container>
  );

  let body: ReactNode;
  if (projects.length === 0) {
    body = (
      <Container>
        <ListingEmptyState {...COUNTRIES_EMPTY} bodyKey="no_projects_country_page_body" />
      </Container>
    );
  } else {
    body = (
      <>
        <ListingGrid>
          {projectsPagination.pageItems.map(project => {
            const tracked = {id: project.uuid, name: project.title};
            return (
              <ListingCard
                key={project.uuid}
                title={project.title}
                image={project.image.src}
                href={projectHref(locale, project.uuid)}
                primaryMeta={project.location ? {icon: LOCATION_ICON, label: project.location} : undefined}
                onClick={() => {
                  trackCountryCardClick(tracked);
                }}
                hoverActions={[
                  {
                    label: t('register_interest'),
                    onClick: () => {
                      trackCountryRegisterInterestClick(tracked);
                      openRegisterInterest({projectId: project.uuid});
                    },
                  },
                  {
                    label: t('view_project'),
                    onClick: () => {
                      trackCountryViewItemClick(tracked, {id: country.uuid, name: country.name});
                      router.push(projectHref(locale, project.uuid));
                    },
                  },
                ]}
              />
            );
          })}
        </ListingGrid>
        {projectsPagination.paginationProps && (
          <Container>
            <ListingPagination {...projectsPagination.paginationProps} />
          </Container>
        )}
      </>
    );
  }

  return (
    <>
      {banner}
      <Section>
        {heading}
        {body}
      </Section>
    </>
  );
}

CountryDetailPage.displayName = 'CountryDetailPage';
